<template>
    <div class="calendar-input-wrapper">
        <div class="calendar-input-header">
            <div class="calendar-input-header-title">
                <div class="calendar-input-header-title-headline">{{ calendarViewMonthName }}</div>
                <div class="calendar-input-header-title-subheadline">{{ calendarViewYearName }}</div>
            </div>
            <div>
                <div class="calendar-input-navigation">
                    <ol class="calendar-input-navigation-list">
                        <li class="calendar-input-navigation-item" @click="previousMonth">
                            <icon icon="hero/outline/chevron-left" class="calendar-input-navigation-item-icon"></icon>
                        </li>
                        <li class="calendar-input-navigation-item" @click="nextMonth">
                            <icon icon="hero/outline/chevron-right" class="calendar-input-navigation-item-icon"></icon>
                        </li>
                    </ol>
                </div>
            </div>
        </div>

        <div>
            <table class="calendar-input-table">
                <thead>
                    <tr>
                        <th v-for="i in 7" :key="i" class="calendar-input-table-header">
                            {{ lang(`calendar.weekdaysShort.${i - 1}`) }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(week, weekindex) in weeks" :key="weekindex">
                        <td v-for="day in week" :key="day" class="calendar-input-table-cell">
                            <div
                                class="calendar-input-table-cell-day"
                                :class="[
                                    {
                                        'calendar-input-table-cell-day--today': day.isToday,
                                        'calendar-input-table-cell-day--other-month': !day.isMonth,
                                        'calendar-input-table-cell-day--selectable':
                                            day.isMonth && !day.isToday && !day.isSelected,
                                        'calendar-input-table-cell-day--selected': day.isSelected,
                                    },
                                ]"
                                @click="selectDay(day)"
                            >
                                {{ day.day }}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { DateTime } from 'luxon';
import { lang } from '@/lang/composable';
import Icon from '@src/Common/Components/Icon.vue';

const emit = defineEmits<{
    (event: 'update:modelValue', content: string | DateTime): void;
    (event: 'picked'): void;
}>();

interface Props {
    modelValue?: string | object | null;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
});

const dt = props.modelValue ? DateTime.fromISO(props.modelValue as string) : DateTime.local();

const today = dt;

const calendarView = ref({
    month: dt.month,
    year: dt.year,
});

const selected = ref(props.modelValue ? DateTime.fromISO(props.modelValue) : null);

const calendarViewMonthName = computed(() => {
    return lang(`calendar.months.${calendarView.value.month - 1}`);
});

const calendarViewYearName = computed(() => {
    return calendarView.value.year;
});

const weeks = computed(() => {
    const startOfMonth = DateTime.local()
        .set({ month: calendarView.value.month, year: calendarView.value.year })
        .startOf('month');
    const endOfMonth = DateTime.local()
        .set({ month: calendarView.value.month, year: calendarView.value.year })
        .endOf('month');

    const startOfCalendar = startOfMonth.startOf('week');
    const endOfCalendar = endOfMonth.endOf('week');

    let step = startOfCalendar;
    let weekNumber = 0;
    const output = [];

    while (step < endOfCalendar) {
        if (!output[weekNumber]) {
            output[weekNumber] = [];
        }

        output[weekNumber].push({
            date: step,
            day: step.day,
            isToday: today.hasSame(step, 'day'),
            isMonth: step.hasSame(startOfMonth, 'month'),
            isSelected: selected.value?.hasSame(step, 'day'),
        });
        step = step.plus({ days: 1 });

        if (step.weekday === 1) {
            weekNumber++;
        }
    }

    return output;
});

const previousMonth = () => {
    if (calendarView.value.month === 1) {
        calendarView.value.month = 12;
        calendarView.value.year--;
    } else {
        calendarView.value.month--;
    }
};

const nextMonth = () => {
    if (calendarView.value.month === 12) {
        calendarView.value.month = 1;
        calendarView.value.year++;
    } else {
        calendarView.value.month++;
    }
};

const selectDay = (day) => {
    selected.value = day.date;

    emit('update:modelValue', selected.value);
    emit('picked');
};
</script>
